import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ProveedorCombobox } from "@/components/ProveedorCombobox";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, AlertCircle, ArrowLeftCircle, Bell, ShieldCheck, Clock, CheckCircle2, XCircle, FileKey2, Trash2, ExternalLink } from "lucide-react";

export default function Regulatory() {
  return (
    <div className="space-y-6" data-testid="regulatory-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#5c7368] mb-1">Regulatorio</div>
        <h1 className="text-3xl font-bold tracking-tight text-[#15241d]" style={{ fontFamily: "'Manrope', sans-serif" }}>Regulatorio</h1>
        <p className="text-sm text-[#4a5c53] mt-1">Cumplimiento SRI, incidencias, devoluciones a proveedor y alertas por política de vencimiento.</p>
      </div>
      <Tabs defaultValue="sri">
        <TabsList className="bg-white border border-[#d1dbd6]">
          <TabsTrigger value="sri"><ShieldCheck size={14} className="mr-2" />Cumplimiento SRI</TabsTrigger>
          <TabsTrigger value="incidencias"><AlertCircle size={14} className="mr-2" />Incidencias</TabsTrigger>
          <TabsTrigger value="devoluciones"><ArrowLeftCircle size={14} className="mr-2" />Devoluciones a proveedor</TabsTrigger>
          <TabsTrigger value="alertas"><Bell size={14} className="mr-2" />Alertas política</TabsTrigger>
        </TabsList>
        <TabsContent value="sri"><SriCompliance /></TabsContent>
        <TabsContent value="incidencias"><Incidencias /></TabsContent>
        <TabsContent value="devoluciones"><Devoluciones /></TabsContent>
        <TabsContent value="alertas"><Alertas /></TabsContent>
      </Tabs>
    </div>
  );
}

function SriCompliance() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = () => {
    setLoading(true);
    api.get("/regulatory/sri-compliance")
      .then(r => setData(r.data))
      .catch(e => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  if (loading) return <div className="mt-4 text-[#8ba397] text-sm">Cargando panel de cumplimiento...</div>;
  if (!data) return null;

  const cert = data.certificado || {};
  const pendientes = data.pendientes_24h || { total: 0, detalle: [] };
  const u30 = data.ultimo_30d || {};
  const elim = data.eliminaciones_recientes || [];

  const certColor = cert.estado === "vigente" ? "#1f6b42" : cert.estado === "por_expirar" ? "#8a5500" : "#7a2514";
  const certBg = cert.estado === "vigente" ? "#def5e6" : cert.estado === "por_expirar" ? "#fef7ec" : "#fdecea";
  const certBorder = cert.estado === "vigente" ? "#9dd4ae" : cert.estado === "por_expirar" ? "#f5c477" : "#d99584";

  return (
    <div className="mt-4 space-y-6" data-testid="sri-compliance-panel">
      <div className="grid md:grid-cols-4 gap-4">
        <div className="rounded-md border p-4" style={{ borderColor: certBorder, background: certBg }}>
          <div className="flex items-center gap-2 mb-2"><FileKey2 size={16} style={{ color: certColor }} /><span className="text-[10px] uppercase tracking-[0.15em] font-semibold" style={{ color: certColor }}>Certificado .p12</span></div>
          {cert.configurado ? (
            <>
              <div className="text-2xl font-bold tabular-nums" style={{ color: certColor }}>{cert.dias_restantes ?? "—"}<span className="text-xs font-normal ml-1">días</span></div>
              <div className="text-[10px] text-[#5c7368]">Expira {cert.expira} · <span className="capitalize font-semibold" style={{ color: certColor }}>{cert.estado?.replace("_", " ")}</span></div>
              {cert.subject && (
                <div className="text-[9px] text-[#8ba397] mt-2 truncate" title={cert.subject}>
                  {cert.subject.split(",").find(p => p.startsWith("CN="))?.replace("CN=", "") || "—"}
                </div>
              )}
            </>
          ) : (
            <div className="text-sm text-[#7a2514] font-semibold">No configurado · configure SRI_CERT_PATH en backend/.env</div>
          )}
        </div>

        <div className="rounded-md border border-[#d1dbd6] bg-white p-4">
          <div className="flex items-center gap-2 mb-2"><Clock size={16} className="text-[#0b66a1]" /><span className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#0b66a1]">Pendientes &gt;24h</span></div>
          <div className={`text-2xl font-bold tabular-nums ${pendientes.total > 0 ? "text-[#7a2514]" : "text-[#1f6b42]"}`}>{pendientes.total}</div>
          <div className="text-[10px] text-[#5c7368]">facturas sin autorizar &gt; 24 horas</div>
        </div>

        <div className="rounded-md border border-[#d1dbd6] bg-white p-4">
          <div className="flex items-center gap-2 mb-2"><CheckCircle2 size={16} className="text-[#1f6b42]" /><span className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#1f6b42]">Tasa autorización 30d</span></div>
          <div className="text-2xl font-bold tabular-nums text-[#1f6b42]">{u30.tasa_autorizacion_pct ?? "—"}<span className="text-xs font-normal ml-1">%</span></div>
          <div className="text-[10px] text-[#5c7368]">{u30.autorizadas || 0}/{u30.total_emitidas || 0} autorizadas</div>
        </div>

        <div className="rounded-md border border-[#d1dbd6] bg-white p-4">
          <div className="flex items-center gap-2 mb-2"><XCircle size={16} className="text-[#7a2514]" /><span className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#7a2514]">Rechazadas 30d</span></div>
          <div className="text-2xl font-bold tabular-nums text-[#7a2514]">{u30.rechazadas || 0}</div>
          <div className="text-[10px] text-[#5c7368]">Borrador: {u30.borrador || 0} · Firmado: {u30.firmado || 0} · Enviado: {u30.enviado || 0}</div>
        </div>
      </div>

      {cert.estado === "por_expirar" && (
        <div className="bg-[#fef7ec] border border-[#f5c477] rounded-md p-3 text-sm text-[#8a5500]" data-testid="cert-expira-warning">
          <AlertCircle size={14} className="inline mr-1" />
          Su certificado .p12 expira en <strong>{cert.dias_restantes} días</strong>. Renueve con el Banco Central del Ecuador o Security Data antes de la fecha de expiración ({cert.expira}) para evitar interrupciones.
        </div>
      )}
      {cert.estado === "expirado" && (
        <div className="bg-[#fdecea] border border-[#d99584] rounded-md p-3 text-sm text-[#7a2514]" data-testid="cert-expirado-error">
          <XCircle size={14} className="inline mr-1" />
          <strong>Certificado expirado</strong> el {cert.expira}. No podrá firmar comprobantes hasta renovarlo.
        </div>
      )}

      <div className="bg-white border border-[#d1dbd6] rounded-md overflow-hidden">
        <div className="px-4 py-2 bg-[#e9f2ee] text-xs uppercase tracking-wider text-[#205c45] font-semibold border-b border-[#d1dbd6] flex items-center justify-between">
          <span>Facturas pendientes de autorización (&gt;24h desde creación)</span>
          <Badge variant="outline" className="text-[10px]">{pendientes.total}</Badge>
        </div>
        <table className="erp-table text-xs">
          <thead><tr><th>Número</th><th>Fecha</th><th>Cliente</th><th>Estado SRI</th><th className="text-right">Total</th><th>Creada</th><th className="w-20"></th></tr></thead>
          <tbody>
            {pendientes.detalle?.map(f => (
              <tr key={f.id} data-testid={`sri-pend-${f.id}`}>
                <td className="font-mono">{f.numero}</td>
                <td>{f.fecha}</td>
                <td>{f.cliente_nombre}</td>
                <td><Badge variant="outline" className="capitalize text-[10px]">{f.estado_sri}</Badge></td>
                <td className="text-right tabular-nums font-semibold">${(f.total || 0).toFixed(2)}</td>
                <td className="text-[10px] text-[#5c7368]">{f.created_at?.slice(0, 10)}</td>
                <td><Link to={`/facturacion?f=${f.id}`}><Button size="sm" variant="outline" data-testid={`sri-pend-go-${f.id}`}><ExternalLink size={12} /></Button></Link></td>
              </tr>
            ))}
            {(!pendientes.detalle || pendientes.detalle.length === 0) && <tr><td colSpan={7} className="text-center py-6 text-[#1f6b42]">✓ Sin pendientes &gt;24h</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="bg-white border border-[#d1dbd6] rounded-md overflow-hidden">
        <div className="px-4 py-2 bg-[#fdecea] text-xs uppercase tracking-wider text-[#7a2514] font-semibold border-b border-[#d1dbd6] flex items-center gap-2">
          <Trash2 size={12} />
          Eliminaciones recientes (auditoría SRI — últimos 20)
        </div>
        <table className="erp-table text-xs">
          <thead><tr><th>Fecha</th><th>Usuario</th><th>Tipo</th><th>Número</th><th>Estado eliminado</th><th>Clave acceso</th></tr></thead>
          <tbody>
            {elim.map((e, i) => (
              <tr key={i}>
                <td>{e.fecha?.slice(0, 19).replace("T", " ")}</td>
                <td>{e.usuario_nombre || e.usuario_id?.slice(0, 8)}</td>
                <td><Badge variant="outline" className="text-[10px]">{e.tabla?.replace("_", " ")}</Badge></td>
                <td className="font-mono">{e.valor_anterior?.numero || "—"}</td>
                <td className="capitalize">{e.valor_anterior?.estado_sri || "—"}</td>
                <td className="font-mono text-[9px] text-[#8ba397]">{e.valor_anterior?.clave_acceso?.slice(0, 12) || "—"}</td>
              </tr>
            ))}
            {elim.length === 0 && <tr><td colSpan={6} className="text-center py-6 text-[#1f6b42]">Sin eliminaciones registradas</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Incidencias() {
  const [items, setItems] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ tipo: "otro", prioridad: "media", descripcion: "", producto_comercial_id: "", lote_id: "", proveedor_id: "" });
  const [prods, setProds] = useState([]);
  const [lotes, setLotes] = useState([]);
  const [provs, setProvs] = useState([]);

  const load = async () => {
    const [i, p, l, pr] = await Promise.all([
      api.get("/regulatory/incidencias"),
      api.get("/masters/productos-comerciales"),
      api.get("/masters/lotes"),
      api.get("/masters/proveedores"),
    ]);
    setItems(i.data); setProds(p.data); setLotes(l.data); setProvs(pr.data);
  };
  useEffect(() => { load().catch(e => toast.error(formatApiError(e))); }, []);

  const save = async () => {
    try {
      const payload = {...form};
      ["producto_comercial_id", "lote_id", "proveedor_id"].forEach(k => { if (!payload[k]) payload[k] = null; });
      await api.post("/regulatory/incidencias", payload);
      toast.success("Incidencia registrada"); setDialogOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="mt-4 space-y-4">
      <div className="flex justify-end"><Button onClick={() => setDialogOpen(true)} className="bg-[#205c45]" data-testid="create-incidencia-btn"><Plus size={16} className="mr-2" />Nueva incidencia</Button></div>
      <div className="rounded-md border border-[#d1dbd6] bg-white overflow-hidden">
        <table className="erp-table">
          <thead><tr><th>Número</th><th>Fecha</th><th>Tipo</th><th>Prioridad</th><th>Descripción</th><th>Reporta</th><th>Estado</th></tr></thead>
          <tbody>
            {items.map(it => (
              <tr key={it.id}>
                <td className="font-mono text-xs">{it.numero || "—"}</td>
                <td>{it.fecha_reporte}</td>
                <td><Badge variant="outline" className="capitalize">{(it.tipo || "otro").replaceAll("_", " ")}</Badge></td>
                <td><Badge className={it.prioridad === "critica" || it.prioridad === "alta" ? "fefo-vencido border" : "fefo-critico border"}>{it.prioridad || "media"}</Badge></td>
                <td className="max-w-md truncate">{it.descripcion}</td>
                <td className="text-xs">{it.usuario_reporta_nombre || "Sistema"}</td>
                <td><Badge className="fefo-safe border capitalize">{(it.estado || "abierta").replaceAll("_", " ")}</Badge></td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-[#8ba397]">Sin incidencias</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>Nueva incidencia regulatoria</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Tipo</Label>
              <Select value={form.tipo} onValueChange={(v) => setForm({...form, tipo: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="producto_incorrecto">Producto incorrecto</SelectItem>
                  <SelectItem value="lote_incorrecto">Lote incorrecto</SelectItem>
                  <SelectItem value="dano">Daño</SelectItem>
                  <SelectItem value="vencimiento">Vencimiento</SelectItem>
                  <SelectItem value="temperatura">Temperatura</SelectItem>
                  <SelectItem value="otro">Otro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Prioridad</Label>
              <Select value={form.prioridad} onValueChange={(v) => setForm({...form, prioridad: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="baja">Baja</SelectItem><SelectItem value="media">Media</SelectItem>
                  <SelectItem value="alta">Alta</SelectItem><SelectItem value="critica">Crítica</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>Producto (opcional)</Label>
              <Select value={form.producto_comercial_id || ""} onValueChange={(v) => setForm({...form, producto_comercial_id: v})}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{prods.map(p => <SelectItem key={p.id} value={p.id}>{p.nombre_comercial}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Proveedor (opcional)</Label>
              <ProveedorCombobox
                value={form.proveedor_id || ""}
                onChange={(v) => setForm({...form, proveedor_id: v})}
                proveedores={provs}
                placeholder="Buscar proveedor…"
                testId="reg-prov"
              />
            </div>
            <div>
              <Label>Lote (opcional)</Label>
              <Select value={form.lote_id || ""} onValueChange={(v) => setForm({...form, lote_id: v})}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{lotes.map(l => <SelectItem key={l.id} value={l.id}>Lote {l.numero_lote}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Descripción</Label><Textarea value={form.descripcion} onChange={(e) => setForm({...form, descripcion: e.target.value})} data-testid="incidencia-descripcion" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button><Button onClick={save} className="bg-[#205c45]" data-testid="save-incidencia-btn">Registrar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Devoluciones() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/regulatory/devoluciones-proveedor").then(r => setItems(r.data)); }, []);
  return (
    <div className="mt-4 rounded-md border border-[#d1dbd6] bg-white overflow-hidden">
      <table className="erp-table">
        <thead><tr><th>Número</th><th>Fecha solicitud</th><th>Proveedor</th><th># Ítems</th><th>Estado</th></tr></thead>
        <tbody>
          {items.map(d => (
            <tr key={d.id}>
              <td className="font-mono text-xs">{d.numero}</td>
              <td>{d.fecha_solicitud}</td>
              <td className="font-medium">{d.proveedor_nombre}</td>
              <td>{d.lineas?.length || 0}</td>
              <td><Badge variant="outline" className="capitalize">{d.estado}</Badge></td>
            </tr>
          ))}
          {items.length === 0 && <tr><td colSpan={5} className="text-center py-10 text-[#8ba397]">Sin devoluciones. Créelas desde las alertas de política.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function Alertas() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/regulatory/alertas-devolucion").then(r => setItems(r.data)); }, []);
  return (
    <div className="mt-4 space-y-4">
      <div className="text-sm text-[#4a5c53]">
        Lotes en la ventana de devolución según la política de cada proveedor. Contacte al proveedor y gestione la devolución antes del vencimiento.
      </div>
      <div className="rounded-md border border-[#d1dbd6] bg-white overflow-hidden">
        <table className="erp-table">
          <thead><tr><th>Producto</th><th>Lote</th><th>Proveedor</th><th>Vencimiento</th><th className="text-right">Días restantes</th><th className="text-right">Disponible</th><th className="text-right">Política (días)</th></tr></thead>
          <tbody>
            {items.map((a, i) => (
              <tr key={i}>
                <td className="font-medium">{a.producto_nombre}</td>
                <td className="font-mono text-xs">{a.numero_lote}</td>
                <td>{a.proveedor_nombre}</td>
                <td>{a.fecha_vencimiento}</td>
                <td className="text-right tabular-nums"><Badge className={a.dias_restantes <= 30 ? "fefo-vencido border" : "fefo-critico border"}>{a.dias_restantes}d</Badge></td>
                <td className="text-right tabular-nums">{a.cantidad_disponible}</td>
                <td className="text-right tabular-nums">{a.dias_politica}</td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-[#8ba397]">Sin alertas por política de devolución</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
